import { debounce } from '../../utils/lodash-replacements';
import ScrollCarousel, { measureSlidesPerView } from '../../components/ScrollCarousel';

const MAIN_SELECTORS = {
  viewport: '[data-carousel-timeline-viewport]',
  track: '[data-carousel-timeline-track]',
};

const NAV_SELECTORS = {
  viewport: '[data-carousel-timeline-nav-viewport]',
  track: '[data-carousel-timeline-nav-track]',
};

const BOTTOM_SELECTORS = {
  viewport: '[data-carousel-timeline-bottom-viewport]',
  track: '[data-carousel-timeline-bottom-track]',
};

const MAIN_CLASSES = {
  viewport: 'MainCarousel__viewport',
  track: 'MainCarousel__track',
  arrow: 'MainCarousel__arrow',
  arrowPrev: 'MainCarousel__arrow--previous',
  arrowNext: 'MainCarousel__arrow--next',
  arrowDisabled: 'MainCarousel__arrow--disabled',
};

const NAV_CLASSES = {
  viewport: 'NavCarousel__viewport',
  track: 'NavCarousel__track',
};

const BOTTOM_CLASSES = {
  viewport: 'BottomCarousel__viewport',
  track: 'BottomCarousel__track',
};

// Flickity's `asNavFor` applied this to the nav cell matching the main
// carousel's selected slide. `.NavCarousel__slide.is-nav-selected` is styled in
// CarouselTimeLine.scss (purple text + purple top border), so the replacement
// sync has to keep applying it by hand.
const NAV_SELECTED_CLASS = 'is-nav-selected';

let mainCarousel = null;
let navCarousel = null;
let bottomCarousel = null;
let mainScrollFrame = null;
let lastLiveIndex = null;
let pendingIndex = null;

/**
 * Re-entrancy guard for the hand-written `asNavFor` replacement.
 *
 * The sync is two-way — main -> nav/bottom on change, and nav/bottom -> main on
 * click. `ScrollCarousel.select()` fires `change`, so without this flag a click
 * on a nav cell would drive main, which would drive nav back, which can fight a
 * scroll that is still settling. Every programmatic cross-carousel move is
 * wrapped in `withSync`.
 */
let syncing = false;

const withSync = (fn) => {
  if (syncing) return;
  syncing = true;
  try {
    fn();
  } finally {
    syncing = false;
  }
};

/** Highlight the nav cell matching the main carousel's selected slide. */
const markNavSelected = (carousel, index) => {
  if (!carousel || !carousel.valid) return;
  carousel.realSlides.forEach((slide, i) => {
    slide.classList.toggle(NAV_SELECTED_CLASS, i === index);
  });
};

/**
 * Flickity moved an `asNavFor` companion so the selected cell stayed in view.
 * `ScrollCarousel.select()` does the same thing via scroll-snap, but only when
 * the companion can actually scroll — for the bottom carousel (one full-width
 * slide) and for a nav that fits entirely on screen this is a no-op.
 */
const followMain = (index) => {
  markNavSelected(navCarousel, index);
  markNavSelected(bottomCarousel, index);

  withSync(() => {
    if (navCarousel && navCarousel.valid) navCarousel.select(index);
    if (bottomCarousel && bottomCarousel.valid) bottomCarousel.select(index);
  });
};

// Flickity emitted `select` as soon as a drag was released. ScrollCarousel's
// `change` waits for snapping to settle, so mirror the active label and move
// the companion carousels from the live scroll position.
const handleMainScroll = () => {
  if (mainScrollFrame !== null) return;

  mainScrollFrame = window.requestAnimationFrame(() => {
    mainScrollFrame = null;
    if (!mainCarousel || !mainCarousel.valid) return;

    const index = mainCarousel.toRealIndex(mainCarousel.rawIndex);

    if (pendingIndex !== null) {
      if (index !== pendingIndex) return;
      pendingIndex = null;
    }

    if (index === lastLiveIndex) return;
    lastLiveIndex = index;
    followMain(index);
  });
};

/** Clicking a companion cell selects the matching main slide. */
const bindNavClicks = (element, carousel) => {
  if (!element || !carousel || !carousel.valid) return;

  element.addEventListener('click', (event) => {
    const slide = event.target.closest('[data-timeline-index]');
    if (!slide || !element.contains(slide)) return;
    // Clones carry no index — `makeClone` strips data attributes — but guard
    // anyway so a future `loop: true` cannot select NaN.
    const index = Number(slide.dataset.timelineIndex);
    if (!Number.isInteger(index)) return;

    if (mainCarousel && mainCarousel.valid) {
      const liveIndex = mainCarousel.toRealIndex(mainCarousel.rawIndex);
      pendingIndex = liveIndex === index ? null : index;
    }

    withSync(() => {
      if (mainCarousel && mainCarousel.valid) mainCarousel.select(index);
    });
    // Outside `withSync` so the highlight still lands: the main carousel's
    // `change` callback is suppressed while the guard is held.
    followMain(index);
  });
};

const initCarousels = () => {
  // Initialize main carousel
  if (!mainCarousel) {
    const mainElement = document.querySelector('[data-carousel-timeline]');
    if (mainElement) {
      mainCarousel = new ScrollCarousel(mainElement, {
        selectors: MAIN_SELECTORS,
        classes: MAIN_CLASSES,
        // Flickity: cellAlign 'left' (== scroll-snap `start`), contain false,
        // draggable true, dragThreshold 10 (native scrolling handles the
        // threshold now), pageDots false, imagesLoaded true (the base class
        // remeasures as late images settle, so no option is needed).
        pageDots: false,
        draggable: true,
        prevNextButtons: true,
        slidesPerView: 1,
        // Autoplay stays off; Flickity never had it here either. Ref. WIN-8070.
        autoPlay: 0,
        ariaLabel: 'Timeline',
        on: {
          change: (index) => {
            const liveIndex = mainCarousel ? mainCarousel.toRealIndex(mainCarousel.rawIndex) : index;
            if (liveIndex !== index) {
              pendingIndex = index;
              return;
            }

            // Suppressed when the change originated from a companion click.
            if (syncing || index === lastLiveIndex) return;
            lastLiveIndex = index;
            followMain(index);
          },
        },
      });
      mainCarousel.viewport.addEventListener('scroll', handleMainScroll, { passive: true });
    }
  }

  // Initialize nav carousel
  if (!navCarousel) {
    const navElement = document.querySelector('[data-carousel-timeline-nav]');
    if (navElement) {
      const viewport = navElement.querySelector(NAV_SELECTORS.viewport);
      const track = navElement.querySelector(NAV_SELECTORS.track);

      if (viewport && track && track.children.length > 0) {
        navCarousel = new ScrollCarousel(navElement, {
          selectors: NAV_SELECTORS,
          classes: NAV_CLASSES,
          // The old config branched on `window.innerWidth > 767` only to switch
          // `cellAlign` ('left' desktop / 'center' mobile) and `contain`.
          // Scroll-snap has no grouping-safe centered alignment, so both
          // breakpoints now snap to `start` — the same accepted limitation as
          // the pods and lookbook carousels (WIN-8070 Risk #10). On mobile the
          // active nav label sits flush left instead of centered.
          prevNextButtons: false,
          pageDots: false,
          draggable: true,
          slidesPerView: measureSlidesPerView(viewport, track.children[0]),
          autoPlay: 0,
          ariaLabel: 'Timeline navigation',
        });
        bindNavClicks(navElement, navCarousel);
      }
    }
  }

  // Initialize bottom carousel
  if (!bottomCarousel) {
    const bottomElement = document.querySelector('[data-carousel-timeline-bottom]');
    if (bottomElement) {
      bottomCarousel = new ScrollCarousel(bottomElement, {
        selectors: BOTTOM_SELECTORS,
        classes: BOTTOM_CLASSES,
        // Flickity: cellAlign 'left', contain false, draggable false,
        // prevNextButtons false, pageDots false. It was purely a slaved
        // read-out of the main carousel's paragraph on mobile.
        prevNextButtons: false,
        pageDots: false,
        draggable: false,
        slidesPerView: 1,
        autoPlay: 0,
        ariaLabel: 'Timeline detail',
      });
      // `draggable: false` under Flickity still allowed clicking a cell to
      // select it via `asNavFor`; preserve that.
      bindNavClicks(bottomElement, bottomCarousel);
    }
  }

  // Flickity applied `.is-nav-selected` as soon as the companions were bound.
  if (mainCarousel && mainCarousel.valid) {
    lastLiveIndex = mainCarousel.selectedIndex;
    followMain(mainCarousel.selectedIndex);
  }
};

const handleResize = debounce(() => {
  // Re-initialize carousels on resize
  if (mainCarousel) {
    mainCarousel.resize();
  }
  if (navCarousel) {
    navCarousel.resize();
  }
  if (bottomCarousel) {
    bottomCarousel.resize();
  }
}, 500);

export default function () {
  // Check if any carousel elements exist
  const mainElement = document.querySelector('[data-carousel-timeline]');
  const navElement = document.querySelector('[data-carousel-timeline-nav]');
  const bottomElement = document.querySelector('[data-carousel-timeline-bottom]');

  if (!mainElement && !navElement && !bottomElement) {
    return;
  }

  // NOTE: this used to register a `DOMContentLoaded` listener unconditionally.
  // The entry is a plain `<script defer>` (snippets/js.liquid), which
  // normally runs before that event, but a deferred script that finishes after
  // DOMContentLoaded has already fired would never have initialized the
  // carousels at all. Ref. WIN-8070.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCarousels, { once: true });
  } else {
    initCarousels();
  }

  // Resize event listener
  window.addEventListener('resize', handleResize);
}
